import { Upload } from "lucide-react";
import { Container } from "./styles";
import { BotaoBranco } from "../../Common/styles";
import { useState, useRef } from "react";
import toast from "react-hot-toast";
import { toastDefs, toastDefsError } from "../../../../config/toastConfig";
import { IAnexo } from "../../../../utility/IAnexo";
import { truncarNomeArquivo } from "../../../../utility/byteSizeUtils";
import { uploadFileWithStateManagement, uploadMultipleFilesWithStateManagement } from "../fileUploadHelpers";

export interface FileUploadInfo {
    file: File;
    progress: number;
    status: 'uploading' | 'success' | 'error';
    error?: string;
}

export default function BlocoAdicionaAnexo(
    {id_proposta, setLoading, uploadingFiles, setUploadingFiles, onAdicionarAnexo, onRecarregarAnexos}: {
        id_proposta?: number, 
        setLoading: (loading: boolean) => void,
        uploadingFiles: FileUploadInfo[],
        setUploadingFiles: React.Dispatch<React.SetStateAction<FileUploadInfo[]>>,
        onAdicionarAnexo: (anexo: IAnexo) => void,
        onRecarregarAnexos?: () => void
    }
) {

    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);


    const uploadSingleFile = async (file: File, index: number) => {
        return uploadFileWithStateManagement({
            file,
            index,
            demandaId: id_proposta,
            setUploadingFiles,
            removalDelay: 3000,
            onSuccessCallback: (responseData) => {
                if (responseData?.result) {
                    onAdicionarAnexo(responseData.result);
                }
            }
        });
    };

    const processFiles = async (files: File[]) => {
        // Check sizes first
        const validFiles = [];
        for (const file of files) {
            if(file.size > 50 * 1024 * 1024) {
                toast.error(`Arquivo ${truncarNomeArquivo(file.name)} excede limite de 50MB`, toastDefsError);
            } else {
                validFiles.push(file);
            }
        }

        if (validFiles.length === 0) {
            return;
        }

        // Initialize all files with 0 progress and uploading status
        const initialFiles: FileUploadInfo[] = validFiles.map(file => ({
            file,
            progress: 0,
            status: 'uploading' as const
        }));
        setUploadingFiles(prev => [...prev, ...initialFiles]);

        // Get the starting index for these files
        const startIndex = uploadingFiles.length;
        
        // Prepare indexes array
        const indexes = validFiles.map((_, idx) => startIndex + idx);

        // Upload all files in a single request
        const result = await uploadMultipleFilesWithStateManagement({
            files: validFiles,
            indexes,
            demandaId: id_proposta,
            setUploadingFiles,
            removalDelay: 3000,
            onSuccessCallback: (responseData) => {
                if (responseData?.result) {
                    onAdicionarAnexo(responseData.result);
                }
            }
        });

        // Count successes and failures
        const successful = result.results.filter(r => r.success).length;
        const failed = result.results.filter(r => !r.success).length;

        // If there's a discrepancy (less results than files), reload the list after a delay
        // to ensure all successfully uploaded files are counted
        if (result.partialSuccess || successful < validFiles.length) {
            // Wait a bit longer to allow backend to process all files
            setTimeout(() => {
                if (onRecarregarAnexos) {
                    onRecarregarAnexos();
                } else {
                    // Fallback: trigger a reload by calling setLoading
                    setLoading(true);
                    setTimeout(() => setLoading(false), 500);
                }
            }, 2000);
        } else if (successful > 0 && onRecarregarAnexos) {
            // Even when all files succeed, reload to ensure count is accurate
            setTimeout(() => {
                onRecarregarAnexos();
            }, 1500);
        }

        // Show appropriate toast
        // Note: Successful files are automatically removed by uploadFileWithStateManagement
        if (failed === 0) {
            toast.success(`${successful} arquivo(s) enviado(s) com sucesso`, toastDefs);
        } else if (successful > 0) {
            toast.success(`${successful} arquivo(s) enviado(s), ${failed} falharam`, toastDefs);
        } else {
            toast.error(`Falha ao enviar ${failed} arquivo(s)`, toastDefsError);
        }
    }

    const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        await processFiles(files);
        if (e.target) {
            e.target.value = '';
        }
    }

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.currentTarget === e.target) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        processFiles(files);
    };

    return (
        <Container 
            $isDragging={isDragging}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            <div className="upload-container">
                <Upload />
            </div>
            <div className="texto-anexos">
                <p className="main">{isDragging ? 'Solte os arquivos aqui' : 'Arraste arquivos para cá'}</p>
                <p className="secundario">ou clique para selecionar arquivos</p>
            </div>
            <div className="input-container">   
                <input 
                    ref={fileInputRef}
                    type="file" 
                    multiple 
                    onChange={handleFiles} 
                    style={{ display: 'none' }} 
                />
                <BotaoBranco onClick={() => fileInputRef.current?.click()}>
                    <Upload />
                    Selecionar Arquivos
                </BotaoBranco>
            </div>
        </Container>
    )
}